import {PostgrestError} from '@supabase/supabase-js'
import {
  CustomToDoType,
  CustomType,
  JournalEventType,
  JournalRecord,
  ToDoType,
  ToDoTypeOption,
} from '../entities/journalRecord'
import {ToDo, ToDoList} from '../entities/todoList'
import {supabase} from './BackendService'
import {cached, cachedById} from './util'
import {markPlantForRevalidation} from '../routes/plantRevalidator'
import {instance as plantsService} from './PlantsService'

export type ToDoListWithToDos = ToDoList & {toDos: ToDo[]}

export const toDoDefaultQuery = `id, creationDateUtc, targetStartDateUtc, targetEndDateUtc, comment, type, importance, plants:plantedPlantsVSToDos( ...plantedPlants(id, name) ), customToDoType:customToDoTypes(id,name,icon)`

// only a custom type has an id of its own, and a deleted one has lost it
function customTypeIdOf(type?: ToDoType) {
  if (!type || type.type !== 'custom' || !type.customToDoType) {
    return null
  }

  return type.customToDoType.id
}

export function transformToDo(databaseToDoRecord: any) {
  const {type, customToDoType, ...rest} = databaseToDoRecord

  if (type === 'custom') {
    return {
      ...databaseToDoRecord,
      type: {
        type,
        customToDoType,
      },
    }
  } else {
    return {
      ...databaseToDoRecord,
      type: {
        type: type,
      },
    }
  }
}

export class ToDoService {
  public getUserToDoLists = cached(async () => {
    let {data: toDoListsWithToDos, error} = await supabase
      .from('toDoLists')
      .select('id, ownerId, name, projectId, toDos(*)')
      .eq('ownerId', (await supabase.auth.getSession()).data.session.user.id)

    return toDoListsWithToDos as ToDoListWithToDos[]
  })

  public getUserUnfinishedToDoLists = cached(async () => {
    let {data: toDoListsWithToDos, error} = await supabase
      .from('toDoLists')
      .select('id, ownerId, name, projectId, toDos(*)')
      .eq('ownerId', (await supabase.auth.getSession()).data.session.user.id)

    return toDoListsWithToDos as ToDoListWithToDos[]
  })

  public getUserUnfinishedToDos = cached(async (): Promise<ToDo[]> => {
    let query = supabase.from('toDos').select(toDoDefaultQuery)

    const {data: toDos, error} = await query
      .order('targetStartDateUtc', {ascending: true})
      .order('type', {ascending: true})

    return (toDos ? toDos.map(transformToDo) : []) as any as ToDo[]
  })

  public getUserUnfinishedPlantsToDos = cached(async () => {
    let query = supabase.from('toDos').select(toDoDefaultQuery).is('toDoListId', null)

    const {data: toDos, error} = await query

    return toDos.map(transformToDo) as any as ToDo[]
  })

  public getUserUnfinishedPlantsToDosByPlantId = cachedById(async (plantId: number) => {
    let query = supabase
      .from('toDos')
      .select(
        `id, creationDateUtc, targetStartDateUtc, targetEndDateUtc, comment, type, importance, plants:plantedPlantsVSToDos!inner( ...plantedPlants(id, name) ), customToDoType:customToDoTypes(id,name,icon)`
      )
      .eq('plants.plantId', plantId)

    const {data: toDos, error} = await query

    return toDos.map(transformToDo) as any as ToDo[]
  })

  public getUserFinishedToDos = cached(async () => {
    let {data: toDos, error} = await supabase
      .from('journalEvents')
      .select('*')
      .eq('type', 'finishedToDo')

    return toDos as JournalRecord[]
  })

  // public async getUserFinishedToDoById(id: number): Promise<JournalRecord> {
  //   let {data: toDoRecord, error} = await supabase
  //     .from('journalEvents')
  //     .select('id, name, startDateUtc, comment, elementId, comment, eventCategory')
  //     .eq('id', id)

  //   return {
  //     eventCategory: toDoRecord[0].eventCategory,
  //     id: toDoRecord[0].id,
  //     name: toDoRecord[0].name,
  //     startDateUtc: toDoRecord[0].startDateUtc,
  //   } as JournalRecord
  // }

  public getProjectUnfinishedToDos = cachedById(async (projectId: number) => {
    // let {data: toDos, error} = await supabase.from('toDos').select('*').eq('projectId', projectId)

    let {data: toDoLists, error} = await supabase
      .from('toDoLists')
      .select(`id, ownerId, projectId, name, toDos(${toDoDefaultQuery})`)
      .eq('ownerId', (await supabase.auth.getSession()).data.session.user.id)
      .eq('projectId', projectId)

    if (!error) {
      return (toDoLists as any).flatMap((l) => l.toDos)
    }

    return [] as ToDo[]
  })

  public async upsertToDo(newToDo: Omit<ToDo, 'id'> & {id?: number}) {
    const {plants, type, ...toDo} = newToDo

    if (plants && plants.length > 0) {
      markPlantForRevalidation()

      for (const plant of plants) {
        plantsService.clearPlantCaches(plant.id)
      }
    }

    const {data, error} = await supabase.functions.invoke('upsert-todo-with-plant-links', {
      method: 'POST',
      body: JSON.stringify({
        ...newToDo,
        type: type?.type,
        customTypeId: customTypeIdOf(type),
      }),
    })

    if (error) {
      return {success: false, error}
    } else {
      this.clearTodoCaches()
      return {success: true}
    }
  }

  public async createToDoList(newToDoList: Omit<ToDoList, 'id'>) {
    const {data, error} = await supabase.from('toDoLists').insert([newToDoList])
    // await new Promise((resolve) => setTimeout(resolve, 2000))

    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public clearTodoCaches = () => {
    this.getProjectUnfinishedToDos.clearAllCached()
    this.getUserFinishedToDos.clearCache()
    this.getUserUnfinishedPlantsToDosByPlantId.clearAllCached()
    this.getUserUnfinishedPlantsToDos.clearCache()
    this.getUserUnfinishedToDoLists.clearCache()
    this.getUserUnfinishedToDos.clearCache()
  }

  public async createUserToDoType(
    newToDoTypeName: string,
    newToDoTypeIcon: string
  ): Promise<
    | {
        success: true
        data: CustomType
      }
    | {
        success: false
        error: PostgrestError
      }
  > {
    const {data, error} = await supabase
      .from('customToDoTypes')
      .insert([
        {
          name: newToDoTypeName,
          icon: newToDoTypeIcon,
          owner_id: (await supabase.auth.getSession()).data.session.user.id,
        },
      ])
      .select('id, name, icon')

    if (error) {
      return {success: false, error}
    } else {
      this.getUserToDoTypes.clearCache()
      return {success: true, data: data[0] as CustomType}
    }
  }

  public async editUserToDoType(newToDoType: CustomType): Promise<
    | {
        success: true
        data: CustomType
      }
    | {
        success: false
        error: PostgrestError
      }
  > {
    const {data, error} = await supabase
      .from('customToDoTypes')
      .update({name: newToDoType.name, icon: newToDoType.icon})
      .eq('id', newToDoType.id)
      .eq('owner_id', (await supabase.auth.getSession()).data.session.user.id)
      .select('id, name, icon')

    if (error) {
      return {success: false, error}
    } else {
      this.getUserToDoTypes.clearCache()
      this.clearTodoCaches()
      return {success: true, data: data[0]}
    }
  }

  public async deleteUserToDoType(id: number): Promise<
    | {
        success: true
      }
    | {
        success: false
        error: PostgrestError
      }
  > {
    const {data, error} = await supabase
      .from('customToDoTypes')
      .delete()
      .eq('id', id)
      .eq('owner_id', (await supabase.auth.getSession()).data.session.user.id)

    if (error) {
      return {success: false, error}
    } else {
      this.getUserToDoTypes.clearCache()
      this.clearTodoCaches()
      return {success: true}
    }
  }

  public getUserToDoTypes = cached(async () => {
    const {data, error} = await supabase
      .from('customToDoTypes')
      .select('id, name, icon')
      .eq('owner_id', (await supabase.auth.getSession()).data.session.user.id)

    if (error) {
      return {success: false, error}
    } else {
      return {
        success: true,
        data: data.map((type) => ({type: 'custom', customToDoType: type})) as CustomToDoType[],
      }
    }
  })

  public async editToDo(currentToDo: ToDo) {
    const {plants, type, ...toDo} = currentToDo
    const {data, error} = await supabase.functions.invoke('upsert-todo-with-plant-links', {
      method: 'POST',
      body: JSON.stringify({
        ...currentToDo,
        type: type?.type ? type.type : null,
        customTypeId: customTypeIdOf(type),
      }),
    })

    if (error) {
      return {success: false, error}
    } else {
      this.clearTodoCaches()
      return {success: true, data}
    }
  }

  public async finishToDo(
    currentToDo: ToDo,
    completionDate: Date,
    eventType: ToDoTypeOption,
    projectId?: number
  ) {
    const userId = (await supabase.auth.getSession()).data.session.user.id

    // generate a separate event for every plant
    const events =
      currentToDo.plants.length === 0
        ? {
            projectId: projectId,
            type: eventType.id ? 'custom' : eventType.name,
            custom_type_id: eventType.id ? eventType.id : null,
            creationDateUtc: currentToDo.creationDateUtc || new Date(),
            startDateUtc: currentToDo.targetStartDateUtc,
            endDateUtc: currentToDo.targetEndDateUtc,
            actualCompletionDateUtc: completionDate,
            comment: currentToDo.comment,
            ownerId: userId,
          }
        : currentToDo.plants.map((plant) => ({
            projectId: projectId,
            type: eventType.id ? 'custom' : eventType.name,
            custom_type_id: eventType.id ? eventType.id : null,
            creationDateUtc: currentToDo.creationDateUtc || new Date(),
            startDateUtc: currentToDo.targetStartDateUtc,
            endDateUtc: currentToDo.targetEndDateUtc,
            actualCompletionDateUtc: completionDate,
            comment: currentToDo.comment,
            plantId: plant.id,
            ownerId: userId,
          }))

    const {data, error} = await supabase.from('journalEvents').insert(events)

    if (error) {
      return {success: false, error}
    } else {
      await this.deleteToDo(currentToDo.id)
      this.clearTodoCaches()
      return {success: true}
    }
  }

  public async editToDoList(currentToDoList: ToDoList) {
    const {data, error} = await supabase
      .from('toDoLists')
      .update([currentToDoList])
      .eq('id', currentToDoList.id)

    if (error) {
      return {success: false, error}
    } else {
      this.clearTodoCaches()
      return {success: true}
    }
  }

  public async deleteToDo(id: number) {
    const {data, error} = await supabase.from('toDos').delete().eq('id', id)

    if (error) {
      return {success: false, error}
    } else {
      this.clearTodoCaches()
      return {success: true}
    }
  }

  public async deleteToDoList(id: number) {
    const {data, error} = await supabase.from('toDoLists').delete().eq('id', id)

    if (error) {
      return {success: false, error}
    } else {
      this.clearTodoCaches()
      return {success: true}
    }
  }
}

export const instance = new ToDoService()
